import React, { useState, useEffect } from 'react';
import './Filter.css';

function Filter({ savedFilters, onApplyFilters, onClose }) {
  const [selectedMonths, setSelectedMonths] = useState(new Set());
  const [activeHemisphere, setActiveHemisphere] = useState('north'); // Default to north
  const [timeRange, setTimeRange] = useState({ start: 0, end: 23 }); // 24-hour range
  const [isTimeFilterActive, setIsTimeFilterActive] = useState(false);
  const [caughtFilter, setCaughtFilter] = useState('both'); // 'caught', 'uncaught', or 'both'

  // Load saved filters when component mounts
  useEffect(() => {
    if (savedFilters) {
      setActiveHemisphere(savedFilters.hemisphere || 'north'); // Default to north if null
      setSelectedMonths(new Set(savedFilters.months));
      setTimeRange(savedFilters.timeRange || { start: 0, end: 23 });
      setIsTimeFilterActive(savedFilters.isTimeFilterActive || false);
      setCaughtFilter(savedFilters.caughtFilter || 'both');
    }
  }, [savedFilters]);
  
  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

  const toggleMonth = (monthIndex) => {
    setSelectedMonths(prev => {
      const newSet = new Set(prev);
      if (newSet.has(monthIndex)) {
        newSet.delete(monthIndex);
      } else {
        newSet.add(monthIndex);
      }
      return newSet;
    });
  };

  const handleHemisphereClick = (hemisphere) => {
    // Always set the clicked hemisphere (no deselection allowed)
    setActiveHemisphere(hemisphere);
  };

  return (
    <div className="filter-modal-overlay" onClick={onClose}>
      <div className="filter-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="filter-modal-header">
          <h3>Filter by Hemisphere, Months and Time</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <div className="filter-modal-body">
          <div className="filter-options-grid">
            <button 
              className={`filter-option ${activeHemisphere === 'north' ? 'active' : ''}`}
              onClick={() => handleHemisphereClick('north')}
            >
              <span className="filter-icon">🌎</span>
              <span className="filter-text">Northern</span>
            </button>
            
            <button 
              className={`filter-option ${activeHemisphere === 'south' ? 'active' : ''}`}
              onClick={() => handleHemisphereClick('south')}
            >
              <span className="filter-icon">🌏</span>
              <span className="filter-text">Southern</span>
            </button>
          </div>

          <div className="filter-months-section">
          <div className="months-header">
            <h4>Select Months</h4>
            <button className="current-month-btn" onClick={() => {
              // Get current month (0-11, where 0 = January, 11 = December)
              const currentMonth = new Date().getMonth();
              // Deselect all months except current month
              setSelectedMonths(new Set([currentMonth]));
            }}>
              Current Month
            </button>
          </div>
          <div className="filter-months-grid">
            {months.map((month, index) => (
              <button
                key={month}
                className={`filter-month-btn ${selectedMonths.has(index) ? 'active' : ''}`}
                onClick={() => toggleMonth(index)}
              >
                {month}
              </button>
            ))}
          </div>
        </div>

        <div className="filter-time-section">
          <div className="time-header">
            <h4>Filter by Time</h4>
            <button 
              className={`time-toggle-btn ${isTimeFilterActive ? 'active' : ''}`}
              onClick={() => setIsTimeFilterActive(!isTimeFilterActive)}
            >
              {isTimeFilterActive ? 'ON' : 'OFF'}
            </button>
          </div>
          
          {isTimeFilterActive && (
            <div className="time-controls">
              <div className="time-inputs">
                <div className="time-input-group">
                  <label>Start Time:</label>
                  <select 
                    value={timeRange.start} 
                    onChange={(e) => setTimeRange(prev => ({ ...prev, start: parseInt(e.target.value) }))}
                    className="time-select"
                  >
                    {Array.from({ length: 24 }, (_, i) => (
                      <option key={i} value={i}>
                        {i === 0 ? '12:00 AM' : 
                         i < 12 ? `${i}:00 AM` : 
                         i === 12 ? '12:00 PM' : 
                         `${i - 12}:00 PM`}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="time-input-group">
                  <label>End Time:</label>
                  <select 
                    value={timeRange.end} 
                    onChange={(e) => setTimeRange(prev => ({ ...prev, end: parseInt(e.target.value) }))}
                    className="time-select"
                  >
                    {Array.from({ length: 24 }, (_, i) => (
                      <option key={i} value={i}>
                        {i === 0 ? '12:00 AM' : 
                         i < 12 ? `${i}:00 AM` : 
                         i === 12 ? '12:00 PM' : 
                         `${i - 12}:00 PM`}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="time-presets">
                <button 
                  className="preset-btn"
                  onClick={() => setTimeRange({ start: 6, end: 18 })}
                >
                  Day (6AM-6PM)
                </button>
                <button 
                  className="preset-btn"
                  onClick={() => setTimeRange({ start: 18, end: 5 })}
                >
                  Night (6PM-5AM)
                </button>
                <button 
                  className="preset-btn"
                  onClick={() => {
                    const currentHour = new Date().getHours();
                    setTimeRange({ start: currentHour, end: currentHour });
                  }}
                >
                  Current Hour
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="filter-caught-section">
          <div className="caught-header">
            <h4>Filter by Collection Status</h4>
          </div>
          
          <div className="caught-options">
            <button 
              className={`caught-option-btn ${caughtFilter === 'both' ? 'active' : ''}`}
              onClick={() => setCaughtFilter('both')}
            >
              Show All
            </button>
            <button 
              className={`caught-option-btn ${caughtFilter === 'caught' ? 'active' : ''}`}
              onClick={() => setCaughtFilter('caught')}
            >
              Caught Only
            </button>
            <button 
              className={`caught-option-btn ${caughtFilter === 'uncaught' ? 'active' : ''}`}
              onClick={() => setCaughtFilter('uncaught')}
            >
              Uncaught Only
            </button>
          </div>
        </div>
        </div>

        <div className="filter-action-buttons">
          <button className="clear-filter-btn" onClick={() => {
            // Clear all filters to default state
            setActiveHemisphere('north');
            setSelectedMonths(new Set());
            setTimeRange({ start: 0, end: 23 });
            setIsTimeFilterActive(false);
            setCaughtFilter('both');
          }}>
            Clear Filter
          </button>
          <div className="right-buttons">
            <button className="cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button className="apply-btn" onClick={() => {
              // Save the selected filters
              const filters = {
                hemisphere: activeHemisphere,
                months: selectedMonths,
                timeRange: timeRange,
                isTimeFilterActive: isTimeFilterActive,
                caughtFilter: caughtFilter
              };
              onApplyFilters(filters);
            }}>
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Filter;